import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const NO_ACTIVE_INDEX = -1;

interface Options {
  itemCount: number;
  onActivate: (index: number) => void;
  resetKey?: string;
  idPrefix: string;
}

export function useListKeyboardNavigation({
  itemCount,
  onActivate,
  resetKey = '',
  idPrefix,
}: Options) {
  const [activeIndex, setActiveIndex] = useState(NO_ACTIVE_INDEX);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => setActiveIndex(NO_ACTIVE_INDEX), [resetKey]);

  const clampedIndex =
    activeIndex === NO_ACTIVE_INDEX || itemCount === 0
      ? NO_ACTIVE_INDEX
      : Math.min(activeIndex, itemCount - 1);

  const moveTo = useCallback((index: number) => {
    setActiveIndex(index);
    listRef.current
      ?.querySelector(`[data-list-index="${index}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (itemCount === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        moveTo(
          clampedIndex === NO_ACTIVE_INDEX ? 0 : (clampedIndex + 1) % itemCount
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveTo(
          clampedIndex === NO_ACTIVE_INDEX
            ? itemCount - 1
            : (clampedIndex - 1 + itemCount) % itemCount
        );
      } else if (event.key === 'Enter' && clampedIndex !== NO_ACTIVE_INDEX) {
        event.preventDefault();
        onActivate(clampedIndex);
      }
    },
    [clampedIndex, itemCount, moveTo, onActivate]
  );

  const getItemId = useCallback(
    (index: number) => `${idPrefix}-item-${index}`,
    [idPrefix]
  );

  return {
    activeIndex: clampedIndex,
    activeItemId:
      clampedIndex === NO_ACTIVE_INDEX ? undefined : getItemId(clampedIndex),
    getItemId,
    listRef,
    listId: `${idPrefix}-list`,
    onKeyDown,
  };
}
